import React, { useState, useEffect, useRef } from 'react';
import { api } from '../../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Plus, Upload, FileText, Search, Trash2, Edit2, 
  Eye, ChevronDown, ChevronUp, Database, RefreshCw,
  AlertCircle, CheckCircle, X, Download, History
} from 'lucide-react';
import { toast } from 'sonner';

export default function AIKnowledgeTab() {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0 });
  
  // Modal states
  const [showAddNote, setShowAddNote] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showView, setShowView] = useState(false);
  const [selectedSource, setSelectedSource] = useState(null);
  const [showMigrate, setShowMigrate] = useState(false);
  
  // Form states
  const [noteForm, setNoteForm] = useState({ title: '', content: '', keywords: '', priority: 'normal' });
  const [uploadForm, setUploadForm] = useState({ title: '', keywords: '', priority: 'normal', file: null });
  const [editForm, setEditForm] = useState({ title: '', content: '', keywords: '', priority: 'normal' });
  
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchSources();
  }, [includeInactive]);

  const fetchSources = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/admin/knowledge?include_inactive=${includeInactive}`);
      setSources(response.data.sources || []);
      setStats({
        total: response.data.total || 0,
        active: response.data.active || 0,
        inactive: response.data.inactive || 0
      });
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Only super-admin can access AI Knowledge Management');
      } else {
        toast.error('Failed to load knowledge sources');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNote = async () => {
    if (!noteForm.title.trim() || !noteForm.content.trim()) {
      toast.error('Title and content are required');
      return;
    }
    
    try {
      const keywords = noteForm.keywords.split(',').map(k => k.trim()).filter(k => k);
      await api.post('/admin/knowledge', {
        title: noteForm.title,
        content: noteForm.content,
        keywords: keywords,
        priority: noteForm.priority
      });
      toast.success('Knowledge note created successfully');
      setShowAddNote(false);
      setNoteForm({ title: '', content: '', keywords: '', priority: 'normal' });
      fetchSources();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create note');
    }
  };

  const handleUpload = async () => {
    if (!uploadForm.title.trim() || !uploadForm.file) {
      toast.error('Title and file are required');
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append('file', uploadForm.file);
      formData.append('title', uploadForm.title);
      formData.append('keywords', uploadForm.keywords);
      formData.append('priority', uploadForm.priority);
      
      await api.post('/admin/knowledge/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Document uploaded and processed successfully');
      setShowUpload(false);
      setUploadForm({ title: '', keywords: '', priority: 'normal', file: null });
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchSources();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    }
  };

  const handleUpdate = async () => {
    if (!selectedSource || !editForm.title.trim()) {
      toast.error('Title is required');
      return;
    }
    
    try {
      const keywords = editForm.keywords.split(',').map(k => k.trim()).filter(k => k);
      await api.put(`/admin/knowledge/${selectedSource.id}`, {
        title: editForm.title,
        content: editForm.content,
        keywords: keywords,
        priority: editForm.priority
      });
      toast.success('Knowledge source updated');
      setShowEdit(false);
      setSelectedSource(null);
      fetchSources();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    }
  };

  const handleToggle = async (source) => {
    try {
      await api.patch(`/admin/knowledge/${source.id}/toggle`, {
        is_active: !source.is_active
      });
      
      if (source.is_active) {
        // Source was just disabled
        toast.success('Source disabled successfully');
        if (!includeInactive) {
          // Update local state to keep showing the item as inactive instead of refetching
          setSources(prev => prev.map(s => 
            s.id === source.id ? { ...s, is_active: false } : s
          ));
          setStats(prev => ({ ...prev, active: prev.active - 1, inactive: prev.inactive + 1 }));
          return;
        }
      } else {
        toast.success('Source enabled successfully');
      }
      fetchSources();
    } catch (error) {
      toast.error('Failed to toggle source');
    }
  };

  const handleDelete = async (source) => {
    if (!window.confirm(`Are you sure you want to PERMANENTLY delete "${source.title}"? This cannot be undone.`)) {
      return;
    }
    
    try {
      await api.delete(`/admin/knowledge/${source.id}`);
      toast.success(`"${source.title}" deleted permanently`);
      // Update local state immediately
      setSources(prev => prev.filter(s => s.id !== source.id));
      setStats(prev => ({
        ...prev,
        total: prev.total - 1,
        active: source.is_active ? prev.active - 1 : prev.active,
        inactive: !source.is_active ? prev.inactive - 1 : prev.inactive
      }));
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete source');
    }
  };

  const handleMigrate = async () => {
    try {
      const response = await api.post('/admin/knowledge/migrate-static');
      const { migrated, skipped, errors } = response.data;
      
      if (migrated.length > 0) {
        toast.success(`Migrated ${migrated.length} knowledge sources`);
      }
      if (skipped.length > 0) {
        toast.info(`${skipped.length} sources already existed (skipped)`);
      }
      if (errors.length > 0) {
        toast.error(`${errors.length} sources failed to migrate`);
      }
      
      setShowMigrate(false);
      fetchSources();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Migration failed');
    }
  };

  const handleExportSingle = (source) => {
    try {
      const exportData = {
        title: source.title,
        type: source.type,
        content: source.content,
        keywords: source.keywords,
        priority: source.priority,
        is_active: source.is_active,
        file_name: source.file_name,
        version: source.version,
        created_at: source.created_at,
        updated_at: source.updated_at
      };
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${source.title.replace(/[^a-zA-Z0-9]/g, '_')}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`"${source.title}" downloaded`);
    } catch (error) {
      toast.error('Failed to download');
    }
  };

  const openEditDialog = (source) => {
    setSelectedSource(source);
    setEditForm({
      title: source.title,
      content: source.content,
      keywords: (source.keywords || []).join(', '),
      priority: source.priority || 'normal'
    });
    setShowEdit(true);
  };

  const openViewDialog = (source) => {
    setSelectedSource(source);
    setShowView(true);
  };

  const filteredSources = sources.filter(s => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      s.title?.toLowerCase().includes(query) ||
      s.content?.toLowerCase().includes(query) ||
      (s.keywords || []).some(k => k.toLowerCase().includes(query))
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-emerald-600 mr-2" />
        <span>Loading knowledge sources...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ai-knowledge-tab">
      {/* Header & Stats */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">AI Knowledge Management</h2>
          <p className="text-sm text-gray-500">Manage the AI assistant's knowledge sources</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Badge variant="outline" className="text-gray-600">
            <Database className="w-3 h-3 mr-1" />
            {stats.total} Total
          </Badge>
          <Badge variant="outline" className="text-emerald-600 border-emerald-200 bg-emerald-50">
            <CheckCircle className="w-3 h-3 mr-1" />
            {stats.active} Active
          </Badge>
          <Badge variant="outline" className="text-gray-400">
            {stats.inactive} Inactive
          </Badge>
        </div>
      </div>

      {/* Actions Bar */}
      <Card className="bg-white">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by title, content, or keywords..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="knowledge-search-input"
              />
            </div>
            
            {/* Include Inactive Toggle */}
            <div className="flex items-center gap-2 px-3 py-2 border rounded-md bg-gray-50">
              <Switch
                id="include-inactive"
                checked={includeInactive}
                onCheckedChange={setIncludeInactive}
              />
              <Label htmlFor="include-inactive" className="text-sm cursor-pointer">
                Show Inactive
              </Label>
            </div>
            
            {/* Action Buttons */}
            <div className="flex gap-2">
              <Button onClick={() => setShowAddNote(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-note-btn">
                <Plus className="w-4 h-4 mr-1" />
                Add Note
              </Button>
              <Button onClick={() => setShowUpload(true)} variant="outline" data-testid="upload-doc-btn">
                <Upload className="w-4 h-4 mr-1" />
                Upload
              </Button>
              <Button onClick={() => setShowMigrate(true)} variant="outline" size="icon" title="Migrate Static Files">
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sources List */}
      <div className="space-y-3">
        {filteredSources.length === 0 ? (
          <Card className="bg-white">
            <CardContent className="py-12 text-center">
              <Database className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">
                {searchQuery ? 'No sources match your search' : 'No knowledge sources yet'}
              </p>
              {!searchQuery && stats.total === 0 && (
                <Button onClick={() => setShowMigrate(true)} variant="link" className="mt-2">
                  Migrate existing static files to database
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          filteredSources.map((source) => (
            <Card key={source.id} className={`bg-white ${!source.is_active ? 'opacity-60' : ''}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-4">
                  {/* Icon */}
                  <div className={`p-2 rounded-lg ${source.type === 'document' ? 'bg-blue-100' : 'bg-purple-100'}`}>
                    <FileText className={`w-5 h-5 ${source.type === 'document' ? 'text-blue-600' : 'text-purple-600'}`} />
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium text-gray-900 truncate">{source.title}</h3>
                      <Badge variant={source.priority === 'high' ? 'destructive' : 'secondary'} className="text-xs">
                        {source.priority}
                      </Badge>
                      {!source.is_active && (
                        <Badge variant="outline" className="text-gray-400">Inactive</Badge>
                      )}
                    </div>
                    
                    <p className="text-sm text-gray-500 line-clamp-2 mb-2">
                      {source.content?.substring(0, 200)}...
                    </p>
                    
                    {/* Keywords */}
                    {source.keywords?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-2">
                        {source.keywords.slice(0, 5).map((kw) => (
                          <Badge key={kw} variant="outline" className="text-xs bg-gray-50">
                            {kw}
                          </Badge>
                        ))}
                        {source.keywords.length > 5 && (
                          <Badge variant="outline" className="text-xs">+{source.keywords.length - 5}</Badge>
                        )}
                      </div>
                    )}
                    
                    {/* Meta */}
                    <div className="flex items-center gap-4 text-xs text-gray-400">
                      <span>Type: {source.type}</span>
                      {source.file_name && <span>File: {source.file_name}</span>}
                      <span>v{source.version || 1}</span>
                      <span>{new Date(source.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-1">
                    <Switch
                      checked={source.is_active}
                      onCheckedChange={() => handleToggle(source)}
                      title={source.is_active ? 'Disable source' : 'Enable source'}
                      data-testid={`toggle-source-${source.id}`}
                    />
                    <Button variant="ghost" size="icon" onClick={() => openViewDialog(source)} title="View" data-testid={`view-source-${source.id}`}>
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleExportSingle(source)} title="Download" data-testid={`download-source-${source.id}`}>
                      <Download className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => openEditDialog(source)} title="Edit" data-testid={`edit-source-${source.id}`}>
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => handleDelete(source)} 
                      title="Delete permanently" 
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-source-${source.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Add Note Dialog */}
      <Dialog open={showAddNote} onOpenChange={setShowAddNote}>
        <DialogContent className="max-w-2xl" data-testid="add-note-dialog">
          <DialogHeader>
            <DialogTitle>Add Knowledge Note</DialogTitle>
            <DialogDescription>Create a new text-based knowledge source for the AI assistant.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="note-title">Title *</Label>
              <Input
                id="note-title"
                value={noteForm.title}
                onChange={(e) => setNoteForm({...noteForm, title: e.target.value})}
                placeholder="e.g., Procurement Mode FAQs"
              />
            </div>
            <div>
              <Label htmlFor="note-content">Content *</Label>
              <Textarea
                id="note-content"
                value={noteForm.content}
                onChange={(e) => setNoteForm({...noteForm, content: e.target.value})}
                placeholder="Enter the knowledge content..."
                className="min-h-[200px]"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="note-keywords">Keywords (comma-separated)</Label>
                <Input
                  id="note-keywords"
                  value={noteForm.keywords}
                  onChange={(e) => setNoteForm({...noteForm, keywords: e.target.value})}
                  placeholder="e.g., svp, threshold, bidding"
                />
              </div>
              <div>
                <Label htmlFor="note-priority">Priority</Label>
                <Select value={noteForm.priority} onValueChange={(v) => setNoteForm({...noteForm, priority: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High (checked first)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddNote(false)}>Cancel</Button>
            <Button onClick={handleCreateNote} className="bg-emerald-600 hover:bg-emerald-700">Create Note</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Document Dialog */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent className="max-w-lg" data-testid="upload-doc-dialog">
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
            <DialogDescription>Upload a PDF, DOCX, TXT, or Excel file to add to the AI's knowledge.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="upload-title">Title *</Label>
              <Input
                id="upload-title"
                value={uploadForm.title}
                onChange={(e) => setUploadForm({...uploadForm, title: e.target.value})}
                placeholder="e.g., GPPB Circular No. 01-2025"
              />
            </div>
            <div>
              <Label htmlFor="upload-file">File *</Label>
              <Input
                id="upload-file"
                type="file"
                ref={fileInputRef}
                accept=".pdf,.docx,.txt,.md,.xlsx,.xls"
                onChange={(e) => setUploadForm({...uploadForm, file: e.target.files?.[0] || null})}
              />
              <p className="text-xs text-gray-500 mt-1">Supported: PDF, DOCX, TXT, MD, XLSX, XLS</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="upload-keywords">Keywords (comma-separated)</Label>
                <Input
                  id="upload-keywords"
                  value={uploadForm.keywords}
                  onChange={(e) => setUploadForm({...uploadForm, keywords: e.target.value})}
                  placeholder="e.g., circular, gppb, 2025"
                />
              </div>
              <div>
                <Label htmlFor="upload-priority">Priority</Label>
                <Select value={uploadForm.priority} onValueChange={(v) => setUploadForm({...uploadForm, priority: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High (checked first)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUpload(false)}>Cancel</Button>
            <Button onClick={handleUpload} className="bg-emerald-600 hover:bg-emerald-700">Upload & Process</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Knowledge Source</DialogTitle>
            <DialogDescription>Update the knowledge source details.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="edit-title">Title *</Label>
              <Input
                id="edit-title"
                value={editForm.title}
                onChange={(e) => setEditForm({...editForm, title: e.target.value})}
              />
            </div>
            <div>
              <Label htmlFor="edit-content">Content</Label>
              <Textarea
                id="edit-content"
                value={editForm.content}
                onChange={(e) => setEditForm({...editForm, content: e.target.value})}
                className="min-h-[200px]"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-keywords">Keywords (comma-separated)</Label>
                <Input
                  id="edit-keywords"
                  value={editForm.keywords}
                  onChange={(e) => setEditForm({...editForm, keywords: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="edit-priority">Priority</Label>
                <Select value={editForm.priority} onValueChange={(v) => setEditForm({...editForm, priority: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High (checked first)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEdit(false)}>Cancel</Button>
            <Button onClick={handleUpdate} className="bg-emerald-600 hover:bg-emerald-700">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={showView} onOpenChange={setShowView}>
        <DialogContent className="max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>{selectedSource?.title}</DialogTitle>
            <DialogDescription className="flex items-center gap-2">
              <Badge variant={selectedSource?.type === 'document' ? 'default' : 'secondary'}>
                {selectedSource?.type}
              </Badge>
              <Badge variant={selectedSource?.priority === 'high' ? 'destructive' : 'outline'}>
                {selectedSource?.priority}
              </Badge>
              {selectedSource?.file_name && (
                <span className="text-gray-500">({selectedSource.file_name})</span>
              )}
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-[50vh] pr-4">
            <div className="space-y-4">
              {selectedSource?.keywords?.length > 0 && (
                <div>
                  <Label className="text-xs text-gray-500">Keywords</Label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {selectedSource.keywords.map((kw, idx) => (
                      <Badge key={kw} variant="outline" className="text-xs">{kw}</Badge>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <Label className="text-xs text-gray-500">Content</Label>
                <pre className="mt-1 p-4 bg-gray-50 rounded-lg text-sm whitespace-pre-wrap font-mono overflow-x-auto">
                  {selectedSource?.content}
                </pre>
              </div>
              {selectedSource?.version_history?.length > 0 && (
                <div>
                  <Label className="text-xs text-gray-500 flex items-center gap-1">
                    <History className="w-3 h-3" /> Version History
                  </Label>
                  <div className="mt-2 space-y-2">
                    {selectedSource.version_history.map((v) => (
                      <div key={`${v.version}-${v.updated_at}`} className="text-xs text-gray-500 p-2 bg-gray-50 rounded">
                        v{v.version} - Updated by {v.updated_by} on {new Date(v.updated_at).toLocaleString()}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowView(false)}>Close</Button>
            <Button onClick={() => { setShowView(false); openEditDialog(selectedSource); }}>
              <Edit2 className="w-4 h-4 mr-1" /> Edit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Migrate Dialog */}
      <Dialog open={showMigrate} onOpenChange={setShowMigrate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Migrate Static Knowledge Files</DialogTitle>
            <DialogDescription>
              This will import all existing static knowledge files (.txt) from the backend into the database.
              Files that already exist in the database will be skipped.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-medium">One-time operation</p>
                <p>This migration only needs to be done once. After migration, all knowledge management should be done through this interface.</p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMigrate(false)}>Cancel</Button>
            <Button onClick={handleMigrate} className="bg-emerald-600 hover:bg-emerald-700">
              <Database className="w-4 h-4 mr-1" /> Start Migration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
